import React, { useEffect } from "react";
import './JobDetailsPage.scss';
import { useParams } from 'react-router-dom';
import PageLoadingSpinner from '../PageLoadingSpinner';
import FileUploadBox from '../FileUploadBox';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store/state/appState';
import { AppDispatch } from '../../store/actions/appDispatch';
import { completeJob, loadJobIfMissing, updateJobChannelSettings } from '../../store/actions/jobsActions';
import {
	JobUploadFileListWithManifest,
	JobUploadFileListWithoutManifest,
	getJobUploadFileLists
} from '../../store/selectors/jobsSelectors';
import uploader from '../../helpers/callAudioUploader';
import CompletedJobDetails from './CompletedJobDetails';
import useIsRequestPending from '../../helpers/useIsRequestPending';
import { RequestStatusType } from '../../store/state/requestStatusState';
import classNames from 'classnames';
import JobChannelSelector from './JobChannelSelector';
import { JobDetails } from '../../models/dto/job';
import JobPageContainer from './JobPageContainer';
import { initializeEngagementUsagePolling } from '../../store/actions/engagementsUsageActions';
import { EngagementCallUsageSummary } from '../../models/dto/engagementCallUsage';
import JobDetailsFileListWithManifest from './JobDetailsFileListWithManifest';
import JobDetailsFileListWithoutManifest from './JobDetailsFileListWithoutManifest';
import { EngagementExpandedSummary } from '../../models/dto/engagement';
import { loadEngagementIfMissing } from '../../store/actions/engagementsActions';

export default function JobDetailsPage() {
	const params = useParams<any>();
	const jobId = parseInt(params.jobId);
	const engagementId = parseInt(params.engagementId);
	const job = useSelector<AppState, JobDetails | undefined>(s => s.jobs.details.get(jobId));
	const engagement = useSelector<AppState, EngagementExpandedSummary | undefined>(s => s.engagements.details.get(engagementId));
	const fileList = useSelector<AppState, JobUploadFileListWithManifest | JobUploadFileListWithoutManifest>(s => getJobUploadFileLists(s, jobId))
	const usage = useSelector<AppState, EngagementCallUsageSummary | nundefined>(s => s.engagementsUsage.byId.get(engagementId)?.usage)
	const dispatch = useDispatch<AppDispatch>();
	const isCompleteRequestPending = useIsRequestPending(RequestStatusType.CompleteJob, jobId);
	const isChannelRequestPending = useIsRequestPending(RequestStatusType.UpdateJobChannelSettings, jobId);
	const areCallsUploading = useSelector<AppState, boolean>(s => s.callAudioUpload.uploads.some(u => u.status === 'uploading' || u.status === 'finishing'));
	
	useEffect(() => {
		if (!job) {
			dispatch(loadJobIfMissing(jobId));
		}
	}, [jobId]);

	useEffect(() => {
		dispatch(loadEngagementIfMissing(engagementId));
	}, [engagementId]);
	
	useEffect(() => {
		dispatch(initializeEngagementUsagePolling(engagementId))
	}, [engagementId]);
	
	if (!job || !engagement)
		return <PageLoadingSpinner />;
	
	const jobName = job.createdWithoutCallManifest ? '' : job.name;
	
	if (job.complete) {
		return (
			<JobPageContainer engagementId={engagementId} jobName={jobName} progressStatus="none">
				<CompletedJobDetails engagementId={engagementId} job={job} />
			</JobPageContainer>
		);
	}
	
	if (!job.audioChannel) {
		return (
			<JobPageContainer engagementId={engagementId} jobName={jobName} progressStatus="upload">
				<JobChannelSelector engagementId={engagementId} 
									jobId={jobId} 
									onChannelsMapped={channel1Type => dispatch(updateJobChannelSettings(jobId, channel1Type))} 
									isChannelRequestPending={isChannelRequestPending} />
			</JobPageContainer>
		);
	}
	
	return (
		<JobPageContainer engagementId={engagementId} jobName={jobName} progressStatus={job.createdWithoutCallManifest ? 'none' : 'upload'}>
			<div className={classNames('job-details-page__container', {'job-details-page__container_3-file-lists': fileList.type === 'withManifest' && !!fileList.failed.length})}>
				{areCallsUploading &&
					<div className="job-details-page__uploading-message-outer-container">
						<div className="job-details-page__uploading-message-inner-container-1">
							<div className="job-details-page__uploading-message-inner-container-2">
								<div className="job-details-page__uploading-message">Uploading files: Don’t close your browser or navigate away from this page</div>
							</div>
						</div>
					</div>
				}
				
				<div className="page-container page-container_wide page-container_no-space-on-bottom">
					<h1 className="h1-mb-1 text-center">{job.createdWithoutCallManifest ? 'Upload your calls' : 'Upload all calls'}</h1>

					{!job.createdWithoutCallManifest && <div className="text-center mb-7">Upload all the calls that are included in your call log</div>}

					{!engagement.showTethrAppIntegration && (
						<div className="flex flex-align-center text-left mb-6">
							<div className="flex-item-grow-1 flex-item-shrink-1">
								<h3>Select calls to upload</h3>
							</div>
							<div className="flex-item-static">
								<button className="btn" disabled={isCompleteRequestPending} onClick={handleCompleteJob}>I’m done uploading</button>
							</div>
						</div>
					)}

					<div className="mb-7">
						<FileUploadBox onFilesSelected={uploadFiles}
									   isDisabled={usage?.isQuotaExceeded}
									   disabledMessage="Looks like you’ve used all the minutes or calls within your trial. No further files can be uploaded at this time."
									   header="Drag and drop your files here or browse:"
									   subHeader="Only mp3 or wav files allowed. Max 200mb per file."
									   accept=".wav, .mp3" />
					</div>

					{fileList.type === 'withoutManifest' && <div className="mb-7"><JobDetailsFileListWithoutManifest jobId={job.id} fileList={fileList} engagement={engagement} /></div>}
				</div>

				{fileList.type === 'withManifest' && <JobDetailsFileListWithManifest jobId={job.id} fileList={fileList} />}
			</div>
		</JobPageContainer>
	);
	
	function handleCompleteJob() {
		dispatch(completeJob(jobId));
	}
	
	function uploadFiles(files: File[]): void {
		// sort files so they upload in 'UI order'
		const sorted = ([...files]).sort((a, b) => {
			return (a.name ?? '').localeCompare(b.name ?? '');
		});
		
		uploader.uploadFiles(sorted, jobId);
	}
}